import { format } from 'date-fns';

import { toIsoUtcTimestamp } from './exchanges';
import { ErrorQueryEvent } from 'src/types/ErrorEventTypes';

// The audit vocabulary the design renders. Everything here is either read
// straight off an existing API or derived deterministically from one — nothing
// in the error path is generated, per the redesign's audit-trust requirement.
export type ConversationAuditState =
  | 'healthy'
  | 'partial_failure'
  | 'unresolved';

export type AuditEventKind =
  | 'intent'
  | 'retrieval'
  | 'api'
  | 'tool'
  | 'step'
  | 'error';

export type AuditEventSeverity = 'high' | 'medium' | 'low';

// A retrieved knowledge source: the drawer cites it as a link, never renders
// the article body.
export interface AuditSource {
  documentId: string;
  link?: string | null;
  title: string;
}

// Request/response for an API or tool call. A missing half carries the reason
// it is missing rather than an empty block, so "not captured" can't read as
// "the call returned nothing".
export type ToolCallStatus = 'completed' | 'error';

export interface AuditPayload {
  request?: unknown;
  requestAbsence?: PayloadAbsence;
  response?: unknown;
  responseAbsence?: PayloadAbsence;
  // Set for tool calls; status is absent when the output gives no signal.
  toolCall?: { status?: ToolCallStatus };
}

export type PayloadAbsence = 'not_captured' | 'redacted' | 'unavailable';

export interface AuditEvent {
  // The coaching blocks applied at this step, not the whole conversation.
  aiInstructionIds?: string[];
  // Errors only: the error service's own description of what went wrong.
  category?: string;
  detail: string;
  // Errors only: the whole recorded event, so the drawer can render the Log
  // page's severity, payload and How-to-fix components against it verbatim.
  error?: ErrorQueryEvent;
  evidence?: string;
  kind: AuditEventKind;
  // Intents only: the picked policy's workflow, the version that ran, and the
  // policy's own name (the sentence recomposes with the version label). The
  // version is absent on archives recorded before versions were stamped.
  workflowLink?: { intentTitle: string; version?: number; workflowId: string };
  // Time from the previous recorded event to this one. Derived from component
  // timestamps — the archive records when each step landed, not how long it
  // took — so it is null wherever there is no preceding event to measure from.
  latencyMs: number | null;
  payload?: AuditPayload;
  severity?: AuditEventSeverity;
  sources?: AuditSource[];
  timestamp: string;
}

export interface AuditExchange {
  events: AuditEvent[];
  // Visitor input to the last agent output of the same exchange.
  latencyMs: number | null;
}

export interface ConversationAudit {
  // Index i corresponds to derived transcript exchange i.
  exchanges: AuditExchange[];
  state: ConversationAuditState;
}

// A failed API call is one record that is both an API event and an error, so
// "is this an error" is a question about the recorded event, not the kind.
export const isErrorAuditEvent = (event: AuditEvent) =>
  event.kind === 'error' || Boolean(event.error);

export const allAuditEvents = (audit: ConversationAudit) =>
  audit.exchanges.flatMap(exchange => exchange.events);

export const countAuditEvents = (
  audit: ConversationAudit,
  kind: AuditEventKind,
) =>
  allAuditEvents(audit).filter(event =>
    kind === 'error' ? isErrorAuditEvent(event) : event.kind === kind,
  ).length;

export const auditErrorCount = (audit: ConversationAudit) =>
  countAuditEvents(audit, 'error');

// Every error in the conversation paired with the exchange it failed in, so the
// rollup can jump each one to its own turn.
export const auditErrorEvents = (audit: ConversationAudit) =>
  audit.exchanges.flatMap((exchange, index) =>
    exchange.events
      .filter(isErrorAuditEvent)
      .map(event => ({ event, exchangeIndex: index })),
  );

export const erroredExchangeIndexes = (audit: ConversationAudit) =>
  audit.exchanges
    .map((exchange, index) =>
      exchange.events.some(isErrorAuditEvent) ? index : -1,
    )
    .filter(index => index >= 0);

// Events inside one turn are seconds apart, so the date on every row is noise;
// the drawer header already carries the day. Recorded timestamps are UTC
// whether or not they say so.
export const formatEventTime = (timestamp: string) =>
  format(new Date(toIsoUtcTimestamp(timestamp)), 'h:mm:ss aaa');

// Persistent per-event latency: under a second reads as milliseconds, above it
// as seconds with a single trimmed decimal (840ms, 1.4s, 10s).
export const formatLatency = (milliseconds: number) => {
  if (milliseconds < 1000) {
    return `${Math.round(milliseconds)}ms`;
  }
  return `${(milliseconds / 1000).toFixed(1).replace(/\.0$/, '')}s`;
};
